import { useLang } from 'hooks/lang';
import { useCallback, useState, lazy, Suspense } from 'react';

import { Column, Loader, Modal, Slider, Button } from 'components/Blocks';
import { ImagesIcon } from 'components/Icons';

import { CroppedAreaProps, executeImageCrop } from './utils';

const Cropper = lazy(() => import('react-easy-crop'));

interface ModalProps {
  onSelection: (base64: string | null) => Promise<void>;

  fileConfig: {
    file: File;
    readResult: string;
  } | null;

  close: () => void;
}

function EditPictureModal({ fileConfig, onSelection, close }: ModalProps): JSX.Element {
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);

  const { t } = useLang();

  const onCropComplete = useCallback(
    (_croppedArea: CroppedAreaProps, croppedAreaPixels: CroppedAreaProps) => {
      if (!fileConfig) return;

      const base64Image = executeImageCrop(croppedAreaPixels, fileConfig);

      if (base64Image) setCroppedImage(base64Image);
    },
    [fileConfig],
  );

  return (
    <Modal
      isOpen={!!fileConfig}
      close={close}
      preventOpenAutoFocus
      className="h-[600px] w-[min(90vw,700px)]"
    >
      <Suspense
        fallback={
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none">
            <Loader type="dualRing" size={32} />
          </div>
        }
      >
        <div className="absolute bottom-24 left-0 right-0 top-0">
          <Cropper
            image={fileConfig?.readResult}
            crop={crop}
            zoom={zoom}
            aspect={4 / 4}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
          />
        </div>

        <Column
          className="absolute bottom-0 left-1/2 h-24 w-1/2 -translate-x-1/2 p-2.5"
          gap="6"
          justifyContent="flex-end"
        >
          <Slider value={zoom} min={1} max={3} step={0.1} onChangeDirect={setZoom} size="small" />

          <Button
            size="small"
            onClick={() => {
              onSelection(croppedImage);
              setZoom(1);
            }}
          >
            <ImagesIcon />
            {t('save')}
          </Button>
        </Column>
      </Suspense>
    </Modal>
  );
}

export default EditPictureModal;
